/**
 * Prize hooks wired to the real `/me/prizes/*` endpoints. See
 * `frontend/docs/learner-gamification-and-prizes.md` for the contract.
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import * as me from '../../api/me';
import type { ClaimStatus } from '../../types/gamification';
import type {
  LearnerPrize,
  LearnerPrizeClaim,
  LearnerPrizeDetail,
  Paginated,
} from '../../types/learnerPrizes';

export const prizeKeys = {
  all: ['me', 'prizes'] as const,
  detail: (id: string) => ['me', 'prize', id] as const,
  claims: (status?: ClaimStatus) => ['me', 'prizes', 'claims', status ?? 'all'] as const,
};

export function usePrizes() {
  return useQuery<LearnerPrize[]>({
    queryKey: prizeKeys.all,
    queryFn: me.getPrizes,
  });
}

export function usePrize(prizeId: string | undefined) {
  return useQuery<LearnerPrizeDetail | null>({
    queryKey: prizeId ? prizeKeys.detail(prizeId) : ['me', 'prize', '__none__'],
    queryFn: async () => {
      if (!prizeId) return null;
      return me.getPrize(prizeId);
    },
    enabled: !!prizeId,
  });
}

export function useClaimPrize() {
  const queryClient = useQueryClient();
  return useMutation<LearnerPrizeClaim, unknown, string>({
    mutationFn: (prizeId) => me.claimPrize(prizeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: prizeKeys.all });
      queryClient.invalidateQueries({ queryKey: ['me', 'prizes', 'claims'] });
      queryClient.invalidateQueries({ queryKey: ['me', 'gamification', 'points'] });
      queryClient.invalidateQueries({ queryKey: ['me', 'gamification', 'leaderboard'] });
      queryClient.invalidateQueries({ queryKey: ['me', 'dashboard'] });
    },
  });
}

interface UsePrizeClaimsOptions {
  status?: ClaimStatus;
  perPage?: number;
}

/**
 * Paginated history of the caller's prize claims. `useInfiniteQuery` so the
 * inline row on `/learn/prizes` can keep loading more without a route push.
 */
/** Learner confirms the prize arrived — pending claim becomes fulfilled. */
export function useMarkClaimReceived() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (claimId: string) => me.markClaimReceived(claimId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: prizeKeys.all });
    },
  });
}

export function usePrizeClaims({ status, perPage = 20 }: UsePrizeClaimsOptions = {}) {
  const query = useInfiniteQuery<Paginated<LearnerPrizeClaim>>({
    queryKey: prizeKeys.claims(status),
    queryFn: ({ pageParam }) =>
      me.getPrizeClaims({
        page: typeof pageParam === 'number' ? pageParam : 1,
        per_page: perPage,
        status,
      }),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.meta.page < last.meta.pages ? last.meta.page + 1 : undefined,
  });

  const pages = (query.data as InfiniteData<Paginated<LearnerPrizeClaim>> | undefined)?.pages ?? [];
  const claims = pages.flatMap((p) => p.data);
  const total = pages[0]?.meta.total ?? 0;

  return {
    claims,
    total,
    isLoading: query.isLoading,
    isError: query.isError,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: !!query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
}
